import { useState, useEffect } from 'react';

/**
 * Hook per gestire il branding personalizzato delle agenzie primarie
 * Propaga le personalizzazioni anche ai nodi subordinati nella gerarchia
 * @param {Object} user - Oggetto utente corrente
 * @param {Object} userNode - Nodo dell'utente nella gerarchia di rete
 * @param {Array} allNodes - Tutti i nodi della rete (per trovare l'agenzia primaria)
 * @returns {Object} Stato del branding e funzioni di controllo
 */
const useBranding = (user, userNode, allNodes = []) => {
  const [branding, setBranding] = useState({
    logoUrl: null,
    colorePrincipale: '#3B82F6',
    attivo: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determina se l'utente è un'agenzia primaria
  const isAgenziaPrimaria = () => {
    if (!user) {
      console.log('🚫 useBranding: Nessun utente trovato');
      return false;
    }
    
    const ruolo = (user.role || user.ruolo || '').toLowerCase();
    // console.log('🔍 useBranding: Utente corrente:', { id: user.id, nome: user.nome, ruolo: ruolo });
    
    // Controlla se è un'agenzia (possibili varianti del ruolo)
    const isAgenzia = ruolo === 'agenzia' || ruolo === 'agenzia_primaria' || ruolo.includes('agenzia');
    // console.log('🎯 useBranding: È agenzia primaria?', isAgenzia);
    
    return isAgenzia;
  };

  // Determina se l'utente è un admin
  const isAdmin = () => {
    if (!user) return false;
    const ruolo = (user.role || user.ruolo || '').toLowerCase();
    return ruolo === 'admin';
  };

  // Trova l'agenzia primaria nella gerarchia
  const findAgenziaPrimaria = () => {
    if (!allNodes.length) {
      console.log('⚠️ useBranding: allNodes non disponibili');
      return null;
    }

    // Se l'utente è admin senza nodo, usa la prima agenzia primaria disponibile
    if (isAdmin() && !userNode) {
      const primaAgenzia = allNodes.find(node => node.tipo === 'agenzia_primaria');
      if (primaAgenzia) {
        console.log('👑 useBranding: Admin senza nodo, uso prima agenzia primaria:', primaAgenzia.id, primaAgenzia.nome);
        return primaAgenzia;
      }
      console.log('⚠️ useBranding: Admin - nessuna agenzia primaria trovata nei nodi');
      return null;
    }

    if (!userNode) {
      console.log('⚠️ useBranding: userNode non disponibile');
      return null;
    }

    // Se l'utente è già un'agenzia primaria, usa il suo nodo
    if (isAgenziaPrimaria() && userNode.tipo === 'agenzia_primaria') {
      console.log('🎯 useBranding: Utente è agenzia primaria, uso il suo nodo:', userNode.id);
      return userNode;
    }

    // Altrimenti, cerca l'agenzia primaria nella gerarchia
    let currentNode = userNode;
    let agenziaPrimaria = null;

    // Risali la gerarchia fino a trovare l'agenzia primaria
    while (currentNode) {
      if (currentNode.tipo === 'agenzia_primaria') {
        agenziaPrimaria = currentNode;
        console.log('🏢 useBranding: Trovata agenzia primaria nella gerarchia:', agenziaPrimaria.id);
        break;
      }
      
      // Trova il nodo padre
      const parentNode = allNodes.find(node => node.id === currentNode.parentId);
      if (!parentNode) {
        console.log('⚠️ useBranding: Nessun padre trovato per nodo:', currentNode.id);
        break;
      }
      
      currentNode = parentNode;
    }

    if (agenziaPrimaria) {
      console.log('✅ useBranding: Agenzia primaria trovata:', agenziaPrimaria.id);
    } else {
      console.log('❌ useBranding: Nessuna agenzia primaria trovata nella gerarchia');
    }

    return agenziaPrimaria;
  };

  // Funzione per caricare il branding dell'agenzia
  const caricaBranding = async () => {
    // Trova l'agenzia primaria (propria o nella gerarchia)
    const agenziaPrimaria = findAgenziaPrimaria();
    
    if (!agenziaPrimaria?.id) {
      console.log('⚠️ useBranding: Nessuna agenzia primaria trovata, uso branding di default');
      setBranding({
        logoUrl: null,
        colorePrincipale: '#3B82F6',
        attivo: false
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`🌐 useBranding: Caricamento branding per agenzia NODO ID: ${agenziaPrimaria.id} (User ID: ${user.id})`);

      // Carica il branding utilizzando l'ID dell'agenzia primaria
      const response = await fetch(`/api/branding/${agenziaPrimaria.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log(`📡 useBranding: Response status: ${response.status}`);
      const data = await response.json();
      console.log('📦 useBranding: Response data:', data);

      if (data.success) {
        const brandingData = {
          logoUrl: data.data.logoUrl,
          colorePrincipale: data.data.colorePrincipale || '#3B82F6',
          attivo: true
        };
        console.log('✅ useBranding: Branding applicato:', brandingData);
        setBranding(brandingData);
      } else {
        console.log('ℹ️ useBranding: Nessun branding trovato, uso default');
        // Branding di default se non trovato
        setBranding({
          logoUrl: null,
          colorePrincipale: '#3B82F6',
          attivo: false
        });
      }
    } catch (err) {
      console.error('❌ useBranding: Errore nel caricamento del branding:', err);
      setError(err.message);
      
      // Branding di default in caso di errore
      setBranding({
        logoUrl: null,
        colorePrincipale: '#3B82F6',
        attivo: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Debug: Traccia i cambiamenti di userNode e allNodes
  useEffect(() => {
    // console.log('🔍 useBranding: userNode cambiato:', userNode);
    // console.log('🔍 useBranding: allNodes disponibili:', allNodes.length);
  }, [userNode, allNodes]);

  // Carica il branding quando cambia l'utente, il nodo o i nodi della rete
  useEffect(() => {
    if (!user?.id || !allNodes.length) {
      console.log('⏳ useBranding: Aspetto dati...', { userId: user?.id, allNodesCount: allNodes.length });
      return;
    }

    const ruolo = (user.role || user.ruolo || '').toLowerCase();
    const utenteAdmin = ruolo === 'admin';

    // Per admin: basta che allNodes siano disponibili (non hanno userNode)
    // Per altri utenti: serve anche userNode
    if (utenteAdmin || userNode?.id) {
      console.log('🔄 useBranding: Trigger caricaBranding', { ruolo, userNodeId: userNode?.id, allNodesCount: allNodes.length });
      caricaBranding();
    } else {
      console.log('⏳ useBranding: Aspetto userNode per utente non-admin...');
    }
  }, [user?.id, user?.role, user?.ruolo, userNode?.id, allNodes]);

  // Funzione per applicare i colori dinamicamente
  const applicaColori = () => {
    // console.log('🎨 useBranding: applicaColori chiamata', { attivo: branding.attivo, colore: branding.colorePrincipale });
    
    if (!branding.attivo || !branding.colorePrincipale) {
      // console.log('⚠️ useBranding: Branding non attivo o colore mancante');
      return;
    }

    // Applica il colore principale come variabile CSS
    document.documentElement.style.setProperty('--colore-agenzia', branding.colorePrincipale);
    // console.log(`🎯 useBranding: Colore CSS applicato: ${branding.colorePrincipale}`);
    
    // Genera varianti del colore per hover, focus, etc.
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    const rgb = hexToRgb(branding.colorePrincipale);
    if (rgb) {
      // Varianti di colore per hover e stati
      document.documentElement.style.setProperty('--colore-agenzia-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
      document.documentElement.style.setProperty('--colore-agenzia-light', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`);
      document.documentElement.style.setProperty('--colore-agenzia-medium', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`);
      console.log('✅ useBranding: Varianti colore applicate');
    }
  };

  // Applica i colori quando il branding cambia
  useEffect(() => {
    applicaColori();
  }, [branding.colorePrincipale, branding.attivo]);

  // Pulisce i colori personalizzati quando il componente si smonta
  useEffect(() => {
    return () => {
      if (branding.attivo) {
        document.documentElement.style.removeProperty('--colore-agenzia');
        document.documentElement.style.removeProperty('--colore-agenzia-rgb');
        document.documentElement.style.removeProperty('--colore-agenzia-light');
        document.documentElement.style.removeProperty('--colore-agenzia-medium');
      }
    };
  }, []);

  return {
    branding,
    loading,
    error,
    isAgenziaPrimaria: isAgenziaPrimaria(),
    caricaBranding,
    // Funzioni helper per l'interfaccia
    getLogo: () => branding.logoUrl || '/logo.png',
    getColorePrincipale: () => branding.colorePrincipale,
    hasCustomLogo: () => branding.attivo && !!branding.logoUrl,
    hasCustomColors: () => branding.attivo && branding.colorePrincipale !== '#3B82F6'
  };
};

export default useBranding; 